import { AbstractService } from '@/services/core/serviceFactory';
import { groupsGraphqlService, clinicsGraphqlService, patientsGraphqlService, type GroupDetails as APIGroupDetails } from '@/services/api';
import type { GraphQLClientResponse } from '@/services/core/graphqlClient';
import { GROUPS_QUERIES } from '@/services/resources/groupsGquery';


export interface Group {
  group_id: number;
  group_name: string;
  es_id: number;
  es_name: string | null;
  group_type: string;
  group_type_id: number;
  clinic_id?: number;
  clinic_name: string;
  updated_time_utc: string;
  patient_clinic_timezone: string;
  is_archive: boolean;
  number_of_patients?: number;
}

export interface GroupsResponse {
  pep_vw_group_details: APIGroupDetails[];
}

export interface ArchiveGroupResponse {
  success: boolean;
  message?: string;
}

export interface PatientGroupAuditLog {
  pep_patient_group_audit_log_id: number;
  patient_group_id: number;
  action_type: string;
  action_datetime: string;
  metadata: any;
}

export class GroupsService extends AbstractService {
  /**
   * Fetch all active groups (non-archived)
   */
  async fetchActiveGroups(): Promise<GraphQLClientResponse<GroupsResponse>> {
    this.requireAuth();

    try {
      return await groupsGraphqlService.getAllActiveGroups(
        this.context.hasuraToken
      ) as GraphQLClientResponse<GroupsResponse>;
    } catch (error) {
      console.error('Error fetching active groups:', error);
      throw new Error('Failed to fetch active groups');
    }
  }

  /**
   * Filter groups by search term
   */
  filterGroups(groups: Group[], searchTerm: string): Group[] {
    if (!searchTerm) return groups;

    const searchTermLower = searchTerm.toLowerCase();
    
    return groups.filter((group: Group) => 
      group.group_name?.toLowerCase().includes(searchTermLower) ||
      group.clinic_name?.toLowerCase().includes(searchTermLower) ||
      group.es_name?.toLowerCase().includes(searchTermLower) ||
      group.group_type?.toLowerCase().includes(searchTermLower)
    );
  }

  /**
   * Sort groups by specified field
   */
  sortGroups(groups: Group[], sortBy: keyof Group, sortOrder: 'asc' | 'desc' = 'desc'): Group[] {
    return [...groups].sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];
      
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      return 0;
    });
  }

  /**
   * Get groups by clinic ID
   */
  getGroupsByClinic(groups: Group[], clinicId: number): Group[] {
    return groups.filter(group => group.clinic_id === clinicId);
  }

  /**
   * Get groups by enrollment specialist ID
   */
  getGroupsByES(groups: Group[], esId: number): Group[] {
    return groups.filter(group => group.es_id === esId);
  }

  /**
   * Fetch all archived groups
   */
  async fetchArchivedGroups(): Promise<GraphQLClientResponse<GroupsResponse>> {
    this.requireAuth();

    try {
      return await groupsGraphqlService.getArchivedGroups(
        this.context.hasuraToken
      ) as GraphQLClientResponse<GroupsResponse>;
    } catch (error) {
      console.error('Error fetching archived groups:', error);
      throw new Error('Failed to fetch archived groups');
    }
  }

  /**
   * Archive or unarchive a group
   */
  async toggleArchiveGroup(groupId: number, toArchive: boolean): Promise<ArchiveGroupResponse> {
    this.requireAuth();
    this.requireUserId();

    if (!groupId) {
      throw new Error('Group ID is required');
    }

    try {
      const endpoint = this.buildPepEndpoint(`/archiveGroup/${groupId}`);
      const response = await this.apiClient.put(endpoint, { to_archive: toArchive }, this.context.token);
      
      if (response.success) {
        return {
          success: true,
          message: `Group ${toArchive ? 'archived' : 'unarchived'} successfully`
        };
      } else {
        return {
          success: false,
          message: response.error || `Failed to ${toArchive ? 'archive' : 'unarchive'} group`
        };
      }
    } catch (error) {
      console.error(`Error ${toArchive ? 'archiving' : 'unarchiving'} group:`, error);
      throw error;
    }
  }

  /**
   * Archive a group
   */
  async archiveGroup(groupId: number): Promise<ArchiveGroupResponse> {
    return this.toggleArchiveGroup(groupId, true);
  }

  /**
   * Unarchive a group
   */
  async unarchiveGroup(groupId: number): Promise<ArchiveGroupResponse> {
    return this.toggleArchiveGroup(groupId, false);
  }

  /**
   * Check if group name already exists
   */
  async checkGroupNameExists(groupName: string): Promise<boolean> {
    this.requireAuth();
    
    try {
      const result = await groupsGraphqlService.checkGroupName(this.context.hasuraToken!, groupName.trim());
      return (result.data?.pep_vw_group_details || []).length > 0;
    } catch (error) {
      console.error('Error checking group name:', error);
      return false; // Allow creation if check fails
    }
  }

  /**
   * Load group details for editing
   */
  async loadGroupDetailsForEdit(groupId: number): Promise<APIGroupDetails | null> {
    this.requireAuth();

    try {
      const result = await groupsGraphqlService.getGroupDetails(this.context.hasuraToken!, { group_id: { _eq: groupId } });
      return result.data?.pep_vw_group_details?.[0] || null;
    } catch (error) {
      console.error('Error loading group details for edit:', error);
      throw new Error('Failed to load group details');
    }
  }

  /**
   * Create a new group - REST API call in feature service
   */
  async createGroup(groupData: any): Promise<ArchiveGroupResponse> {
    this.requireUserId();
    this.requireAuth();

    try {
      const endpoint = this.buildPepEndpoint('/ga');
      const response = await this.apiClient.post(endpoint, groupData, this.context.token);
      
      if (response.success) {
        return {
          success: true,
          message: 'Group created successfully'
        };
      } else {
        return {
          success: false,
          message: response.error || 'Failed to create group'
        };
      }
    } catch (error) {
      console.error('Error creating group:', error);
      return {
        success: false,
        message: 'Failed to create group'
      };
    }
  }

  /**
   * Update an existing group - REST API call in feature service
   */
  async updateGroup(groupId: number, groupData: any): Promise<ArchiveGroupResponse> {
    this.requireUserId();
    this.requireAuth();

    try {
      const endpoint = this.buildPepEndpoint(`/ga/${groupId}`);
      const response = await this.apiClient.put(endpoint, groupData, this.context.token);
      
      if (response.success) {
        return {
          success: true,
          message: 'Group updated successfully'
        };
      } else {
        return {
          success: false,
          message: response.error || 'Failed to update group'
        };
      }
    } catch (error) {
      console.error('Error updating group:', error);
      return {
        success: false,
        message: 'Failed to update group'
      };
    }
  }

  /**
   * Load group patient list - business logic for group patient management
   */
  async loadGroupPatientList(groupId: number) {
    this.requireAuth();

    try {
      // Get patient list from group using API service
      const groupResult = await groupsGraphqlService.getGroupPatientList(this.context.hasuraToken!, groupId);
      
      if (!groupResult.data?.pep_vw_group_patient_list?.length) {
        return { patientList: [], patients: [] };
      }

      const patientList = groupResult.data.pep_vw_group_patient_list[0].patient_list || [];
      
      if (patientList.length === 0) {
        return { patientList: [], patients: [] };
      }

      // Get detailed patient data using API service
      const patientsResult = await patientsGraphqlService.getConsolidatedData(
        this.context.hasuraToken!,
        { patient_clinic_data_id: { _in: patientList } },
        [{ full_name: 'asc' }]
      );

      const patients = patientsResult.data?.pep_vw_consolidated_data || [];

      return { patientList, patients };
    } catch (error) {
      console.error('Error loading group patient list:', error);
      throw new Error('Failed to load group patient list');
    }
  }


  /**
   * Load group types - delegates to API service  
   */
  async loadGroupTypes(): Promise<any[]> {
    this.requireAuth();
    try {
      const result = await groupsGraphqlService.getGroupTypes(this.context.hasuraToken!);
      return result.data?.pep_group_type_mapping || [];
    } catch (error) {
      console.error('Error loading group types:', error);
      throw new Error('Failed to load group types');
    }
  }

  /**
   * Fetch patient group dashboard data
   */
  async fetchPatientGroupDashboard(): Promise<GraphQLClientResponse<{ pep_vw_patient_group_dashboard: PatientGroup[] }>> {
    this.requireAuth();

    try {
      return await groupsGraphqlService.getPatientGroupDashboard(
        this.context.hasuraToken
      );
    } catch (error) {
      console.error('Error fetching patient group dashboard:', error);
      throw new Error('Failed to fetch patient group dashboard');
    }
  }

  /**
   * Fetch patient group audit log
   */
  async getPatientGroupAuditLog(patientGroupId: number): Promise<GraphQLClientResponse<{ pep_patient_group_audit_log: PatientGroupAuditLog[] }>> {
    this.requireAuth();

    try {
      const result = await this.graphqlClient.query<{ 
        pep_patient_group_audit_log: PatientGroupAuditLog[];
        pep_patient_group_audit_log_aggregate: { aggregate: { count: number } };
      }>(
        GROUPS_QUERIES.GET_PATIENT_GROUP_AUDIT_LOG,
        { patientGroupId },
        this.context.hasuraToken
      );
      
      return result;
    } catch (error) {
      console.error('Error fetching patient group audit log:', error);
      throw new Error('Failed to fetch patient group audit log');
    }
  }
}

export interface PatientGroup {
  group_name: string;
  is_archived: boolean;
  patient_count: number;
  patient_group_id: number;
  group_filters?: string | null;
}